/**
 * Overview for a v2 model: headline figures, the balance check and the AI summary.
 *
 * The valuation used to sit here too, in a second column. It moved to
 * `valuation/ValuationOverviewV2` when a discipline became something the user
 * chooses first: an accounting user has no reason to meet a discounted cash flow,
 * and the valuation has a discipline of its own to live in.
 *
 * The balance check is shown first and unconditionally. A non-zero check means
 * every figure below it is unreliable, and burying that under the headline
 * numbers would invite the user to act on figures the model itself does not
 * stand behind.
 */

import { useState } from 'react';
import { Markdown } from '../Markdown';
import { AttentionPanel } from './AttentionPanel';
import { FiguresToCheckPanel } from './FiguresToCheckPanel';
import { AutofixPanel } from './AutofixPanel';
import { SourcesPanel } from './SourcesPanel';
import { ChecksPanel } from './ChecksPanel';
import { BalanceDiagnosisPanel } from './BalanceDiagnosisPanel';
import { BalanceFromTextPanel } from './BalanceFromTextPanel';
import { ImportSteps } from './ImportSteps';
import { ImportReviewPanel } from './ImportReviewPanel';
import { CollapsibleNotes } from '../CollapsibleNotes';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type { FinancialModelV2 } from '../../../types/financial-model-v2';
import { formatMoney } from '../../../lib/currency';
import { getForecastContext } from '../../../types/financial-model';

function money(value: number | null | undefined, currency: string): string {
  if (value == null) return '—';
  return formatMoney(value, currency);
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="mt-1 font-mono text-xl font-medium tabular-nums text-gray-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}


export function OverviewViewV2() {
  const [showAssumptions, setShowAssumptions] = useState(false);
  const model = useFinancialModelV2Store((s) => s.model);
  const generateSummary = useFinancialModelV2Store((s) => s.generateSummary);
  const isGenerating = useFinancialModelV2Store((s) => s.isGeneratingSummary);

  if (!model) return null;

  const ctx = getForecastContext(model.settings);
  const currency = model.settings.baseCurrency;
  const { pnl, balanceSheet, annualSummary } = model.computed;

  const lastYear = ctx.fiscalYears[ctx.fiscalYears.length - 1];
  const lastPeriod = ctx.allQuarters[ctx.allQuarters.length - 1];
  const finalYear = annualSummary?.years[lastYear];

  // The two questions the baseline model's overview opens with, asked the same way here.
  const unbalanced = Object.entries(balanceSheet.balanceCheck ?? {}).filter(
    ([, value]) => Math.abs(value) >= 1,
  );
  const importPoints = model.importReport?.length ?? 0;
  const historical = new Set(model.settings.historicalYears ?? []);

  return (
    <div className="h-full overflow-auto p-4 sm:p-6">
      {/* First, before any figure: why the sheet does not balance and what the filing
          does not explain, ordered and largest first. Renders nothing for a clean model. */}
      <AttentionPanel />
      {/* Then each figure the filing does not vouch for, with what would settle it. */}
      <FiguresToCheckPanel />
      <AutofixPanel />
      <SourcesPanel inputs={model.inputs} />
      <ChecksPanel />
      <div className="grid gap-3 sm:grid-cols-2">
        <div
          className={
            unbalanced.length
              ? 'rounded-xl border border-red-200 bg-red-50 px-4 py-3'
              : 'rounded-xl border border-green-200 bg-green-50 px-4 py-3'
          }
        >
          <div className="text-sm font-semibold text-gray-900">
            {unbalanced.length ? 'Out of balance' : 'Balances in every year'}
          </div>
          <div className="mt-0.5 text-xs text-gray-600">
            {unbalanced.length
              ? unbalanced
                  .map(([period, value]) => period.replace('_', ' ') + ': ' + money(value, currency))
                  .join(' · ')
              : 'Assets less liabilities equals equity in every period - cash comes from the cash flow, not a plug.'}
          </div>
        </div>
        <div
          className={
            importPoints
              ? 'rounded-xl border border-amber-200 bg-amber-50 px-4 py-3'
              : 'rounded-xl border border-gray-200 bg-white px-4 py-3'
          }
        >
          <div className="text-sm font-semibold text-gray-900">
            {importPoints
              ? importPoints + (importPoints === 1 ? ' point' : ' points') + ' to check from the import'
              : 'Nothing to check from an import'}
          </div>
          <div className="mt-0.5 text-xs text-gray-600">
            {importPoints
              ? 'What the reading could not vouch for, listed below - worth reading before trusting the figures.'
              : 'Built by hand or imported cleanly.'}
          </div>
        </div>
      </div>

      {/* Import caveats belong here, not on the project list: importing takes
          the user straight into the workspace, so anything rendered back there
          is never seen. These say what could NOT be read from the filing, and
          are worth most before the user starts trusting the figures. */}
      {model.importReport && model.importReport.length > 0 && (
        <div className="mt-6">
          <CollapsibleNotes
            title="Points to check from the import"
            notes={model.importReport.map((item) =>
              String((item as { message?: unknown }).message ?? item),
            )}
          />
        </div>
      )}

      {/* Beneath the import notes and above the figures, which is the order the work
          happens in: read what could not be read, correct what is uncertain, then look at
          the numbers. The prose notes above say what the import could not do; this says
          which individual figures to distrust, and lets them be fixed. */}
      <ImportReviewPanel />

      {/* The diagnosis panel replaces a bare 'does not balance' banner. Telling
          the user the sheet is broken without saying which line broke it
          leaves them no better off than the balance check row already does. */}
      {/* Two columns, because they are two different kinds of work.
          The accounts are tied line by line to Mike's workbook by the
          reconciliation gate. The valuation is this product's own addition
          and has no such reference. Reading down one column tells you what
          the company did; reading down the other tells you what somebody
          thinks it is worth. Running them together as one scroll made the
          second look like a continuation of the first. */}
      <div className="mt-6">
        <div>
          {/* The three steps first, because they say where this model IS before the
              panels say what is wrong with it. Renders nothing for a model built by
              hand rather than imported. */}
          <div className="mb-3">
            <ImportSteps />
          </div>
          <BalanceDiagnosisPanel />
          {/* Directly under the diagnosis, because the two answer one question from
              opposite ends: the diagnosis says which articulation check fails, and this
              says what the filing's own text has to say about the figures in it. It
              renders nothing when the sheet balances. */}
          <div className="mt-3">
            <BalanceFromTextPanel />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Stat
          label={`Revenue (FY ${lastYear})`}
          value={money(finalYear?.totalRevenue, currency)}
        />
        <Stat
          label={`EBITDA (FY ${lastYear})`}
          value={money(finalYear?.ebitda, currency)}
          sub={
            finalYear ? `${(finalYear.ebitdaMargin * 100).toFixed(1)}% margin` : undefined
          }
        />
        <Stat
          label={`Profit after tax (FY ${lastYear})`}
          value={money(finalYear?.profitAfterTax, currency)}
        />
          <Stat
            label="Closing cash"
            value={money(balanceSheet.cashAtBank[lastPeriod], currency)}
            sub={lastPeriod?.replace('_', ' ')}
          />
          </div>

        <div className="mt-6">
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              {/* The columns were unlabelled: six rows of figures with nothing
                  saying which period each belonged to. The order comes from
                  ctx.allQuarters, the same source the body iterates, so the
                  heading cannot drift from the cells under it. */}
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-xs">
                  <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900">
                    Summary
                  </th>
                  {ctx.allQuarters.map((q) => {
                    const year = Number(q.split('_')[1]);
                    return (
                      <th
                        key={q}
                        scope="col"
                        className="px-4 py-2 text-right font-semibold text-gray-900 whitespace-nowrap"
                      >
                        {q.replace('_', ' ')}
                        <div className="text-[10px] font-normal uppercase tracking-wide text-gray-500">
                          {historical.has(year) ? 'Historical' : 'Forecast'}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ['Total revenue', pnl.totalRevenue],
                  ['Cost of sales (negative)', pnl.costOfSales],
                  ['Gross profit', pnl.grossProfit],
                  ['Total indirect costs', pnl.totalIndirectCosts],
                  ['Operating profit', pnl.operatingProfit],
                  ['Profit after tax', pnl.profitAfterTax],
                ].map(([label, series]) => (
                  <tr key={label as string}>
                    <td className="px-4 py-1.5 text-gray-700">{label as string}</td>
                    {ctx.allQuarters.map((q) => (
                      <td key={q} className="px-4 py-1.5 text-right font-mono text-xs tabular-nums text-gray-900">
                        {((series as Record<string, number>)[q] ?? 0).toLocaleString(
                          'en-US',
                          { maximumFractionDigits: 0 },
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>

      {/* Full width, below both columns: the summary reads the whole model,
          so it belongs to neither. */}
      <div className="mt-6">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">AI summary</h3>
          <button
            type="button"
            disabled={isGenerating}
            onClick={() => void generateSummary()}
            className="rounded-md border border-accent-200 bg-accent-50 px-3 py-1 text-xs font-medium text-accent-700 hover:bg-accent-100 disabled:opacity-40"
          >
            {isGenerating ? 'Generating…' : model.aiSummary ? 'Regenerate' : 'Generate'}
          </button>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-800">
          {model.aiSummary ? (
            <Markdown>{model.aiSummary}</Markdown>
          ) : (
            <span className="text-gray-500">
              No summary yet. Generating one reads the whole model, including
              the COGS, Indirect Costs and Fixed Assets tabs.
            </span>
          )}
        </div>
      </div>

    </div>
  );
}
